import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import useWorkspaceSubscriptionFeatures from "../../hooks/useWorkspaceSubscriptionFeatures";
import {
  hasSubscriptionFeature,
  SUBSCRIPTION_FEATURE_KEYS,
} from "../../workspaces/subscriptionFeatures";

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const recordItems = (payload) => {
  const items = pageItems(payload);
  if (items.length) return items;
  return payload && typeof payload === "object" ? [payload] : [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const joinName = (...values) => values.map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const residentName = (resident = {}) =>
  firstText(
    resident.name,
    resident.fullName,
    resident.residentName,
    joinName(resident.firstName, resident.middleName, resident.lastName),
    resident.email,
    "Unnamed resident",
  );

const residentId = (resident = {}) => resident.id || resident.residentId || resident.clientId || "";

const residentEmail = (resident = {}) => firstText(resident.email, resident.primaryContactEmail);

const careProfileId = (profile = {}) => profile.id || profile.careProfileId || profile.profileId || "";

const careProfileName = (profile = {}) => {
  const user = profile.userProfile || profile.user || {};
  const base = profile.profile || profile.profileData || {};
  return firstText(
    user.name,
    user.fullName,
    profile.name,
    profile.fullName,
    joinName(profile.firstName, profile.middleName, profile.lastName),
    base.fullName,
    joinName(base.firstName, base.middleName, base.lastName),
    joinName(user.firstName, user.middleName, user.lastName),
    profile.userName,
    careProfileEmail(profile),
    "Care staff",
  );
};

const careProfileEmail = (profile = {}) => {
  const user = profile.userProfile || profile.user || {};
  const base = profile.profile || profile.profileData || {};
  return firstText(user.email, base.email, profile.email);
};

const processId = (process = {}) => process.id || process.assessmentProcessId || process.processId || "";

const residentIdFromProcess = (process = {}) =>
  process.residentId ||
  process.clientId ||
  process.resident?.id ||
  process.resident?.residentId ||
  process.residentData?.id ||
  process.residentData?.residentId ||
  process.residentDetails?.id ||
  process.residentDetails?.residentId ||
  "";

const processResidentName = (process = {}, residentsById = {}) => {
  const linkedResident = residentsById[residentIdFromProcess(process)];
  if (linkedResident) return residentName(linkedResident);

  return firstText(
    process.resident?.name,
    process.resident?.fullName,
    joinName(process.resident?.firstName, process.resident?.middleName, process.resident?.lastName),
    process.residentData?.name,
    process.residentData?.fullName,
    joinName(process.residentData?.firstName, process.residentData?.middleName, process.residentData?.lastName),
    process.residentDetails?.name,
    process.residentDetails?.fullName,
    joinName(process.residentDetails?.firstName, process.residentDetails?.middleName, process.residentDetails?.lastName),
    process.residentName,
    process.residentFullName,
    process.clientName,
    "Unnamed resident",
  );
};

const statusOf = (record = {}) =>
  String(record.workflowStatus || record.processStatus || record.status || record.state || "")
    .trim()
    .toUpperCase();

const isOpenStatus = (status) => !["ARCHIVED", "COMPLETED", "COMPLETE", "DECLINED", "CANCELED", "CANCELLED", "DELETED"].includes(status);

const dateValue = (record = {}) =>
  record.updatedAt ||
  record.createdAt ||
  record.noteDate ||
  record.startDateTime ||
  record.startTime ||
  record.processStartDate ||
  record.startDate ||
  record.assessmentDate ||
  "";

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfToday = () => {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
};

const isoDate = (date) => date.toISOString().slice(0, 10);

const sameLocalDay = (value, expected = new Date()) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return (
    date.getFullYear() === expected.getFullYear() &&
    date.getMonth() === expected.getMonth() &&
    date.getDate() === expected.getDate()
  );
};

const formatDateTime = (value) => {
  if (!value) return "Recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatTodayLabel = () =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date());

const formatTimeRange = (item = {}) => {
  const start = new Date(item.startDateTime || item.startTime || item.start || item.shiftStart || "");
  const end = new Date(item.endDateTime || item.endTime || item.end || item.shiftEnd || "");
  if (Number.isNaN(start.getTime())) return "Today";
  const timeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
  return Number.isNaN(end.getTime()) ? timeFormat.format(start) : `${timeFormat.format(start)} - ${timeFormat.format(end)}`;
};

const countByStatus = (items, statuses) =>
  items.filter((item) => statuses.includes(statusOf(item))).length;

const actionCardsFor = (canManageWorkspace, hasFeature) => {
  const commonActions = [
    hasFeature("residents") ? ["ti-users", "Residents", "Review resident profiles, contacts, notes, and care documents.", "blue", "/residents"] : null,
    hasFeature("schedules") ? ["ti-calendar", "My Schedule", "See your shifts and care coverage for the selected workspace.", "amber", "/my-schedule"] : null,
    hasFeature("attendance") ? ["ti-clock", "My Clocking", "Clock in, clock out, and review your attendance status.", "blue", "/my-attendance"] : null,
    hasFeature("timesheets") ? ["ti-receipt", "My Timesheets", "Create or review your own timesheet records.", "purple", "/my-timesheet"] : null,
    ["ti-user-heart", "My Care Profile", "Keep your care worker profile and onboarding details current.", "amber", "/care-profile"],
  ].filter(Boolean);

  if (!canManageWorkspace) return commonActions;

  return [
    hasFeature("residents") ? ["ti-user-plus", "Add a New Resident", "Start intake and onboarding for a prospective resident.", "blue", "/residents/new"] : null,
    hasFeature("assessments") ? ["ti-clipboard-plus", "Start Assessment", "Create or continue an assessment process for resident care.", "purple", "/assessments"] : null,
    hasFeature("schedules") ? ["ti-calendar-plus", "Schedules", "Plan staff shifts and review care coverage.", "amber", "/schedules"] : null,
    hasFeature("attendance") ? ["ti-clock-check", "Staff ClockInOut", "Review attendance records and current clock status.", "blue", "/attendance"] : null,
    hasFeature("timesheets") ? ["ti-file-dollar", "Timesheets", "Review, approve, and manage staff timesheets.", "purple", "/timesheets"] : null,
    hasFeature("incidentReporting") ? ["ti-alert-triangle", "Incident Reporting", "Capture incidents, actions taken, notifications, and follow-up.", "amber", "/incidents"] : null,
    hasFeature("cbhsSupportiveSupervisionTracking") ? ["ti-user-shield", "CBHS Supervision", "Track supportive supervision and monthly CBHS report readiness.", "purple", "/cbhs"] : null,
    // hasFeature("rnDelegationWorkflows") ? ["ti-stethoscope", "RN Delegation", "Manage delegated nursing tasks, reviews, training, and signatures.", "blue", "/rn-delegations"] : null,
    // hasFeature("mealPlanning") ? ["ti-tools-kitchen-2", "Care Planning", "Plan meals, activities, ADL routines, and care tasks.", "amber", "/care-planning"] : null,
    hasFeature("weeklyCaregiverHoursTracking") ? ["ti-clock-dollar", "Hours Operations", "Review caregiver hours, overtime, corrections, and disputes.", "purple", "/hours-operations"] : null,
    hasFeature("documents") ? ["ti-files", "Documents", "Upload and manage public or internal workspace documents.", "amber", "/downloads"] : null,
    hasFeature("secureCareTeamDocumentExchange") ? ["ti-mail-lock", "Secure Exchange", "Track secure care-team email, fax, and document exchange.", "blue", "/secure-care-team-exchange"] : null,
    // hasFeature("familyPortal") ? ["ti-users-group", "Family Portal", "Manage family updates, shared documents, and signature requests.", "amber", "/family-portal"] : null,
    hasFeature("schedules") ? ["ti-calendar", "My Schedule", "See your own shifts in this workspace.", "amber", "/my-schedule"] : null,
    hasFeature("attendance") ? ["ti-clock", "My Clocking", "Clock in, clock out, and review your own attendance status.", "blue", "/my-attendance"] : null,
    hasFeature("timesheets") ? ["ti-receipt", "My Timesheets", "Create or review your own timesheet records.", "purple", "/my-timesheet"] : null,
  ].filter(Boolean);
};

const emptyHomeData = {
  residents: [],
  assessments: [],
  dailyNotes: [],
  schedules: [],
  timesheets: [],
  attendance: [],
  careProfile: null,
};

export default function HomePage() {
  const { user, careRoles = [] } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [homeData, setHomeData] = useState(emptyHomeData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [residentSearch, setResidentSearch] = useState("");
  const [careStaffSearch, setCareStaffSearch] = useState("");
  const [residentSearchResults, setResidentSearchResults] = useState([]);
  const [careStaffSearchResults, setCareStaffSearchResults] = useState([]);
  const [residentSearchLoading, setResidentSearchLoading] = useState(false);
  const [careStaffSearchLoading, setCareStaffSearchLoading] = useState(false);
  const [residentSearchError, setResidentSearchError] = useState("");
  const [careStaffSearchError, setCareStaffSearchError] = useState("");

  const actorId = user?.id || user?.userId || "";
  const normalizedCareRoles = useMemo(() => careRoles.map(normalizeRole), [careRoles]);
  const canManageWorkspace = normalizedCareRoles.includes("care_admin") || normalizedCareRoles.includes("care_owner");
  const workspaceName = firstText(selectedWorkspace?.name, selectedWorkspace?.workspaceName, selectedWorkspace?.businessName, "Workspace");
  const { features: subscriptionFeatures, tier: subscriptionTierName } = useWorkspaceSubscriptionFeatures();
  const hasFeature = useMemo(
    () => (feature) => hasSubscriptionFeature(subscriptionFeatures, feature),
    [subscriptionFeatures],
  );
  const featureKeys = SUBSCRIPTION_FEATURE_KEYS;

  useEffect(() => {
    let active = true;

    const loadHome = async () => {
      if (!workspaceListReady) return;
      if (!selectedWorkspaceId) {
        setHomeData(emptyHomeData);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const todayStart = isoDate(startOfToday());
      const todayEnd = isoDate(endOfToday());
      const workspaceParams = { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId };
      const actorParams = { ...workspaceParams, actorId, userId: actorId };
      const canUseAssessments = hasFeature(featureKeys.assessments);
      const canUseCarePlans = hasFeature(featureKeys.carePlans);
      const canUseSchedules = hasFeature(featureKeys.schedules);
      const canUseTimesheets = hasFeature(featureKeys.timesheets);
      const canUseAttendance = hasFeature(featureKeys.attendance);

      const mineResult = await Promise.allSettled([
        actorId ? careService.careProfiles.getMine(actorParams) : Promise.resolve(null),
      ]);
      const careProfile = mineResult[0]?.status === "fulfilled" ? mineResult[0].value : null;
      const careProfileId = careProfile?.id || careProfile?.careProfileId || "";

      const requests = [
        careService.residents.list(workspaceParams),
        canUseAssessments ? careService.assessmentProcesses.list(workspaceParams) : Promise.resolve([]),
        canUseCarePlans ? careService.dailyNotes.list({ ...workspaceParams, startDate: todayStart, endDate: todayEnd }) : Promise.resolve([]),
        !canUseSchedules
          ? Promise.resolve([])
          : canManageWorkspace || !careProfileId
          ? careService.schedules.list({ ...workspaceParams, startDate: todayStart, endDate: todayEnd })
          : careService.schedules.listForCareProfile(careProfileId, { ...workspaceParams, startDate: todayStart, endDate: todayEnd }),
        !canUseTimesheets
          ? Promise.resolve([])
          : canManageWorkspace || !careProfileId
          ? careService.timesheets.list({ ...workspaceParams, actorId, startDate: todayStart, endDate: todayEnd })
          : careService.timesheets.listForCareProfile(careProfileId, { ...workspaceParams, actorId, startDate: todayStart, endDate: todayEnd }),
        canUseAttendance && careProfileId
          ? careService.attendance.currentForCareProfile(careProfileId, workspaceParams)
          : Promise.resolve(null),
      ];

      const results = await Promise.allSettled(requests);
      if (!active) return;

      const fulfilled = (index, fallback) => (results[index]?.status === "fulfilled" ? results[index].value : fallback);
      const failedCount = results.filter((result) => result.status === "rejected").length + mineResult.filter((result) => result.status === "rejected").length;

      setHomeData({
        residents: pageItems(fulfilled(0, [])),
        assessments: pageItems(fulfilled(1, [])),
        dailyNotes: pageItems(fulfilled(2, [])),
        schedules: pageItems(fulfilled(3, [])),
        timesheets: pageItems(fulfilled(4, [])),
        attendance: recordItems(fulfilled(5, [])),
        careProfile,
      });
      setError(failedCount ? "Some home data could not be loaded. The available sections are shown below." : "");
      setLoading(false);
    };

    loadHome().catch((nextError) => {
      if (!active) return;
      setHomeData(emptyHomeData);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load home data.");
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [actorId, canManageWorkspace, featureKeys.assessments, featureKeys.attendance, featureKeys.carePlans, featureKeys.schedules, featureKeys.timesheets, hasFeature, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    const query = residentSearch.trim();
    if (!selectedWorkspaceId || query.length < 3) {
      setResidentSearchResults([]);
      setResidentSearchLoading(false);
      setResidentSearchError("");
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setResidentSearchLoading(true);
      setResidentSearchError("");
      careService.residents
        .quickSearch({
          wid: selectedWorkspaceId,
          workspaceId: selectedWorkspaceId,
          query,
          size: 8,
        })
        .then((payload) => {
          if (!active) return;
          setResidentSearchResults(pageItems(payload).slice(0, 8));
        })
        .catch((err) => {
          if (!active) return;
          setResidentSearchResults([]);
          setResidentSearchError(err?.response?.data?.message || err?.message || "Unable to search residents.");
        })
        .finally(() => {
          if (active) setResidentSearchLoading(false);
        });
    }, 280);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [residentSearch, selectedWorkspaceId]);

  useEffect(() => {
    const query = careStaffSearch.trim();
    if (!selectedWorkspaceId || query.length < 3) {
      setCareStaffSearchResults([]);
      setCareStaffSearchLoading(false);
      setCareStaffSearchError("");
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setCareStaffSearchLoading(true);
      setCareStaffSearchError("");
      careService.careProfiles
        .quickSearch({
          wid: selectedWorkspaceId,
          workspaceId: selectedWorkspaceId,
          query,
          size: 8,
        })
        .then((payload) => {
          if (!active) return;
          setCareStaffSearchResults(pageItems(payload).slice(0, 8));
        })
        .catch((err) => {
          if (!active) return;
          setCareStaffSearchResults([]);
          setCareStaffSearchError(err?.response?.data?.message || err?.message || "Unable to search care staff.");
        })
        .finally(() => {
          if (active) setCareStaffSearchLoading(false);
        });
    }, 280);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [careStaffSearch, selectedWorkspaceId]);

  const residentsById = useMemo(() => {
    const records = {};
    homeData.residents.forEach((resident) => {
      const id = residentId(resident);
      if (id) records[id] = resident;
    });
    return records;
  }, [homeData.residents]);

  const openAssessments = homeData.assessments.filter((assessment) => isOpenStatus(statusOf(assessment)));
  const activeResidents = homeData.residents.filter((resident) => {
    const status = statusOf(resident);
    return status === "ACTIVE" || status === "CURRENT" || status === "ADMITTED";
  });
  const prospectiveResidents = homeData.residents.filter((resident) => ["PROSPECTIVE", "NEW", "PENDING"].includes(statusOf(resident)));
  const todaySchedules = homeData.schedules.filter((schedule) =>
    sameLocalDay(schedule.startDateTime || schedule.startTime || schedule.startDate || schedule.date, new Date()),
  );
  const submittedTimesheets = countByStatus(homeData.timesheets, ["SUBMITTED", "PENDING_REVIEW", "PENDING REVIEW"]);
  const openAttendance = homeData.attendance.find((record) => !record.clockOutAt && !record.clockOutTime && isOpenStatus(statusOf(record)));

  const metricCards = [
    hasFeature(featureKeys.residents) ? ["ti-users-group", "Residents", homeData.residents.length, `${activeResidents.length} active`, "blue", "/residents"] : null,
    hasFeature(featureKeys.assessments) ? ["ti-clipboard-check", "Assessments", homeData.assessments.length, `${openAssessments.length} open`, "purple", "/assessments"] : null,
    hasFeature(featureKeys.schedules) ? ["ti-calendar-event", "Schedules Today", todaySchedules.length, "Current filter date", "amber", canManageWorkspace ? "/schedules" : "/my-schedule"] : null,
    hasFeature(canManageWorkspace ? featureKeys.timesheets : featureKeys.attendance) ? [
      canManageWorkspace ? "ti-file-dollar" : "ti-clock-check",
      canManageWorkspace ? "Timesheets to Review" : "Clock Status",
      canManageWorkspace ? submittedTimesheets : openAttendance ? "In" : "Out",
      canManageWorkspace ? "Submitted today" : openAttendance ? "Currently clocked in" : "Not clocked in",
      "blue",
      canManageWorkspace ? "/timesheets" : "/my-attendance",
    ] : null,
  ].filter(Boolean);

  const recentActivity = useMemo(() => {
    const assessmentRows = homeData.assessments.map((assessment) => ({
      icon: "ti-clipboard-check",
      title: `${processResidentName(assessment, residentsById)} assessment`,
      detail: statusOf(assessment) ? statusOf(assessment).replace(/_/g, " ") : "Assessment process updated",
      badge: statusOf(assessment) ? statusOf(assessment).replace(/_/g, " ") : "Assessment",
      time: dateValue(assessment),
      to: processId(assessment) ? `/assessments/${processId(assessment)}/new` : "/assessments",
    }));

    const noteRows = homeData.dailyNotes.map((note) => ({
      icon: "ti-note",
      title: firstText(note.title, "Daily note"),
      detail: firstText(note.category, note.residentName, "Daily care note"),
      badge: "Note",
      time: dateValue(note),
      to: note.residentId ? `/residents/${note.residentId}#daily-notes` : "/residents",
    }));

    const scheduleRows = homeData.schedules.map((schedule) => ({
      icon: "ti-calendar-time",
      title: firstText(schedule.careProfileName, schedule.staffName, schedule.workerName, "Staff schedule"),
      detail: `${formatTimeRange(schedule)} · ${statusOf(schedule) || "SCHEDULED"}`,
      badge: statusOf(schedule) || "Scheduled",
      time: dateValue(schedule),
      to: canManageWorkspace ? "/schedules" : "/my-schedule",
    }));

    return [...assessmentRows, ...noteRows, ...scheduleRows]
      .sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime())
      .slice(0, 6);
  }, [canManageWorkspace, homeData.assessments, homeData.dailyNotes, homeData.schedules, residentsById]);

  const actions = actionCardsFor(canManageWorkspace, hasFeature);
  const residentSearchReady = residentSearch.trim().length >= 3;
  const careStaffSearchReady = careStaffSearch.trim().length >= 3;

  return (
    <CareShell className="dashboard-content workspace care-home-page">
      <section className="home-action-panel">
        <div className="home-action-head">
          <div className="page-title">
            <span className="section-eyebrow">Workspace home</span>
            <h1>{workspaceName}</h1>
            <p>
              {canManageWorkspace
                ? "Manage residents, assessments, schedules, documents, and staff care operations."
                : "Review your care work, shifts, attendance, and resident care activity."}
            </p>
          </div>
          <div className="home-status-card">
            <span className={`round-icon ${openAttendance ? "blue" : "amber"}`}>
              <i className={`ti ${openAttendance ? "ti-clock-check" : canManageWorkspace ? "ti-layout-dashboard" : "ti-clock"}`} />
            </span>
            <div>
              <strong>{openAttendance ? "Clocked in" : canManageWorkspace ? "Operations ready" : "Ready to clock in"}</strong>
              <small>{formatTodayLabel()}</small>
            </div>
            <Link className="mini-btn" to={canManageWorkspace ? "/dashboard" : "/my-attendance"}>
              {canManageWorkspace ? "Dashboard" : "Clocking"} <i className="ti ti-arrow-right" />
            </Link>
          </div>
        </div>
        <div className="metric-grid home-overview-metrics" aria-label="Care overview">
          {metricCards.map(([icon, label, value, note, tone, to]) => (
            <Link className="metric-card" to={to} key={label}>
              <span className={`round-icon ${tone}`}><i className={`ti ${icon}`} /></span>
              <div><strong>{label}</strong><b>{loading ? "..." : value}</b><small>{note}</small></div>
            </Link>
          ))}
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="home-search-grid" aria-label="Resident and care staff search">
          {hasFeature(featureKeys.residents) ? (
            <section className="home-search-card">
              <div className="home-search-card-head">
                <span className="round-icon blue"><i className="ti ti-user-search" /></span>
                <div>
                  <strong>Resident Search</strong>
                  <small>Find residents by name or email</small>
                </div>
              </div>
              <label className="home-search-field">
                <i className="ti ti-search" />
                <input value={residentSearch} onChange={(event) => setResidentSearch(event.target.value)} placeholder="Search residents" />
              </label>
              <div className="home-search-results">
                {!residentSearchReady ? <p>Type at least 3 characters to search residents.</p> : residentSearchLoading ? <p>Searching residents...</p> : residentSearchError ? <p>{residentSearchError}</p> : residentSearchResults.length ? residentSearchResults.map((resident) => {
                  const id = residentId(resident);
                  return (
                    <Link className="home-search-result-row" to={id ? `/residents/${id}` : "/residents"} key={id || residentName(resident)}>
                      <span>{residentName(resident)}</span>
                      <small>{residentEmail(resident) || "No email added"}</small>
                      <i className="ti ti-chevron-right" />
                    </Link>
                  );
                }) : <p>No residents found.</p>}
              </div>
            </section>
          ) : null}
          <section className="home-search-card">
            <div className="home-search-card-head">
              <span className="round-icon amber"><i className="ti ti-users-search" /></span>
              <div>
                <strong>Care Staff Search</strong>
                <small>Find care givers by name or email</small>
              </div>
            </div>
            <label className="home-search-field">
              <i className="ti ti-search" />
              <input value={careStaffSearch} onChange={(event) => setCareStaffSearch(event.target.value)} placeholder="Search care staff" />
            </label>
            <div className="home-search-results">
              {!careStaffSearchReady ? <p>Type at least 3 characters to search care staff.</p> : careStaffSearchLoading ? <p>Searching care staff...</p> : careStaffSearchError ? <p>{careStaffSearchError}</p> : careStaffSearchResults.length ? careStaffSearchResults.map((profile) => {
                const id = careProfileId(profile);
                return (
                  <Link className="home-search-result-row" to={id ? `/staff/${id}` : "/care-profile"} key={id || careProfileName(profile)}>
                    <span>{careProfileName(profile)}</span>
                    <small>{careProfileEmail(profile) || "No email added"}</small>
                    <i className="ti ti-chevron-right" />
                  </Link>
                );
              }) : <p>No care staff found.</p>}
            </div>
          </section>
        </div>
        <div className="home-section-head">
          <h2>Quick actions</h2>
          <span>{canManageWorkspace ? "Workspace operations" : "My care tools"}</span>
        </div>
        {actions.length ? (
          <div className="action-grid">
            {actions.map(([icon, title, text, tone, to]) => (
              <Link className="action-card" to={to} key={title}>
                <span className={`round-icon ${tone}`}><i className={`ti ${icon}`} /></span>
                <div><h2>{title}</h2><p>{text}</p></div>
                <i className="ti ti-arrow-right arrow" />
              </Link>
            ))}
          </div>
        ) : (
          <Panel title="Subscription features">
            <p>Your {subscriptionTierName.toLowerCase()} plan does not include additional care tools for this workspace.</p>
            {canManageWorkspace ? <Link className="text-action" to="/workspaces/current/subscriptions">Manage subscription <i className="ti ti-arrow-right" /></Link> : null}
          </Panel>
        )}
      </section>

      <div className="split-grid">
        <Panel title={canManageWorkspace ? "Workspace activity" : "My care activity"} action="Live">
          {loading ? <p>Loading home activity...</p> : recentActivity.length ? recentActivity.map((item) => (
            <Link className="list-row" to={item.to} key={`${item.title}-${item.time}-${item.detail}`}>
              <span className="tiny-icon"><i className={`ti ${item.icon}`} /></span>
              <div><strong>{item.title}</strong><small>{item.detail} · {formatDateTime(item.time)}</small></div>
              <em>{item.badge}</em>
            </Link>
          )) : <p>No recent care activity found for this workspace.</p>}
          <Link className="text-action" to="/dashboard">Open dashboard <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title={canManageWorkspace ? "Care priorities" : "My work today"} action={isoDate(new Date())}>
          {canManageWorkspace ? (
            <>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-user-plus" /></span>
                <div><strong>{prospectiveResidents.length} prospective residents</strong><small>Residents still in intake or onboarding state</small></div>
                <em>Review</em>
              </div>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-clipboard-list" /></span>
                <div><strong>{openAssessments.length} open assessments</strong><small>Assessment processes that still need action</small></div>
                <em>Active</em>
              </div>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-note" /></span>
                <div><strong>{homeData.dailyNotes.length} daily notes today</strong><small>Notes captured for resident care today</small></div>
                <em>Today</em>
              </div>
            </>
          ) : (
            <>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-calendar-event" /></span>
                <div><strong>{todaySchedules.length} scheduled shifts</strong><small>{todaySchedules[0] ? formatTimeRange(todaySchedules[0]) : "No shift found for today"}</small></div>
                <em>Schedule</em>
              </div>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-clock" /></span>
                <div><strong>{openAttendance ? "Clocked in" : "Not clocked in"}</strong><small>{openAttendance ? formatDateTime(openAttendance.clockInAt || openAttendance.clockInTime || openAttendance.createdAt) : "Clock in when your shift starts"}</small></div>
                <em>{openAttendance ? "Active" : "Ready"}</em>
              </div>
              <div className="list-row">
                <span className="tiny-icon"><i className="ti ti-receipt" /></span>
                <div><strong>{homeData.timesheets.length} timesheet records today</strong><small>Submit your care hours from your timesheet page</small></div>
                <em>Hours</em>
              </div>
            </>
          )}
        </Panel>

      </div>
    </CareShell>
  );
}
